"use client";

import { useState } from "react";
import { BookOpen, FileJson } from "lucide-react";
import { CARD } from "./agenticStyle";
import { MCP_PATH } from "../lib/paths";
import { CodeBlock, CopyButton } from "./CopyButton";
import { useOrigin } from "./useOrigin";

type ClientId = "claude-code" | "claude-desktop" | "cursor" | "vscode" | "rest" | "python";

const CLIENTS: { id: ClientId; label: string }[] = [
  { id: "claude-code", label: "Claude Code" },
  { id: "claude-desktop", label: "Claude Desktop" },
  { id: "cursor", label: "Cursor" },
  { id: "vscode", label: "VS Code" },
  { id: "rest", label: "curl" },
  { id: "python", label: "Python" },
];

const PLACEHOLDER = "spk_your_key_here";

function snippets(origin: string): Record<ClientId, { where: string; code: string; note?: string }> {
  const mcp = `${origin}${MCP_PATH}`;
  return {
    "claude-code": {
      where: "In a terminal",
      code: `claude mcp add --transport http steampeek ${mcp} \\\n  --header "Authorization: Bearer ${PLACEHOLDER}"`,
      note: "Add --scope user to have it in every project. Then ask Claude about any Steam game.",
    },
    "claude-desktop": {
      where: "Settings → Developer → Edit config (claude_desktop_config.json)",
      code: JSON.stringify(
        {
          mcpServers: {
            steampeek: {
              command: "npx",
              args: ["-y", "mcp-remote", mcp, "--header", "Authorization:${STEAMPEEK_AUTH}"],
              env: { STEAMPEEK_AUTH: `Bearer ${PLACEHOLDER}` },
            },
          },
        },
        null,
        2,
      ),
      note: "Needs Node.js. mcp-remote bridges the desktop app to a remote server; restart Claude Desktop after saving.",
    },
    cursor: {
      where: "~/.cursor/mcp.json (or .cursor/mcp.json in a project)",
      code: JSON.stringify({ mcpServers: { steampeek: { url: mcp, headers: { Authorization: `Bearer ${PLACEHOLDER}` } } } }, null, 2),
    },
    vscode: {
      where: ".vscode/mcp.json (VS Code asks for the key once and stores it securely)",
      code: JSON.stringify(
        {
          inputs: [{ type: "promptString", id: "steampeek-key", description: "steampeek API key", password: true }],
          servers: {
            steampeek: { type: "http", url: mcp, headers: { Authorization: "Bearer ${input:steampeek-key}" } },
          },
        },
        null,
        2,
      ),
    },
    rest: {
      where: "Any HTTP client",
      code: `export STEAMPEEK_API_KEY=${PLACEHOLDER}\n\ncurl -H "Authorization: Bearer $STEAMPEEK_API_KEY" \\\n  "${origin}/api/v1/games/search?q=hades"`,
      note: "Every response carries X-Units-Charged, and a call that cost units also X-Units-Remaining-Month, so a script always knows where it stands.",
    },
    python: {
      where: "pip install requests",
      code: `import os, requests\n\nAPI = "${origin}/api/v1"\nheaders = {"Authorization": f"Bearer {os.environ['STEAMPEEK_API_KEY']}"}\n\nr = requests.get(f"{API}/games/1145360/players", headers=headers,\n                 params={"from": "2026-09-01"})\nr.raise_for_status()\nfor point in r.json()["data"][:5]:\n    print(point["timestamp"], point["players"])`,
    },
  };
}

const PROMPTS = [
  "Which games gained the most players in the last 24 hours, and what do they have in common?",
  "Compare Hades II's first month of players with Hades.",
  "What's releasing next week with the most followers?",
  "Forecast launch players for an upcoming game on my wishlist, and show which launches it was compared with.",
];

export function ConnectPanel() {
  const origin = useOrigin();
  const mcpUrl = `${origin}${MCP_PATH}`;
  const [client, setClient] = useState<ClientId>("claude-code");
  const s = snippets(origin)[client];
  return (
    <section id="connect" className={`${CARD} scroll-mt-24`} aria-labelledby="connect-heading">
      <h2 id="connect-heading" className="text-lg font-semibold text-white">
        Connect an AI agent
      </h2>
      <p className="mt-1 text-sm text-[#8f98a0]">
        steampeek runs a hosted MCP server: point an agent at it with your key and it can look up games, history, charts,
        releases and publishers on its own. Tool calls cost the same units as the REST calls behind them.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-xs text-[#8f98a0]">Server URL</span>
        <code className="min-w-0 break-all rounded bg-[#0b121a] px-2 py-1 font-mono text-[12.5px] text-white">{mcpUrl}</code>
        <CopyButton text={mcpUrl} label="Copy URL" />
      </div>

      <div className="mt-4 -mx-1 overflow-x-auto px-1" role="tablist" aria-label="Client">
        <div className="flex w-max gap-1 rounded-lg border border-[#2a475e] bg-[#1b2838] p-1">
          {CLIENTS.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={c.id === client}
              onClick={() => setClient(c.id)}
              className={`whitespace-nowrap rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                c.id === client ? "bg-[#66c0f4] text-white" : "text-[#8f98a0] hover:text-white"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3" role="tabpanel">
        <p className="mb-2 text-xs text-[#8f98a0]">{s.where}</p>
        <CodeBlock code={s.code} />
        {s.note && <p className="mt-2 text-xs text-[#8f98a0]">{s.note}</p>}
        <p className="mt-2 text-xs text-[#5c6b7a]">
          Replace <code className="font-mono">{PLACEHOLDER}</code> with one of your keys.
        </p>
      </div>

      <div className="mt-6">
        <h3 className="text-sm font-semibold text-white">Things to ask once it&apos;s connected</h3>
        <ul className="mt-2 grid gap-2 sm:grid-cols-2">
          {PROMPTS.map((p) => (
            <li key={p} className="rounded-lg border border-[#2a475e]/70 bg-[#16202d] px-3 py-2 text-sm text-[#c7d5e0]">
              &ldquo;{p}&rdquo;
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 flex flex-wrap gap-3 text-sm">
        <a href="/docs/api" className="inline-flex items-center gap-1.5 font-medium text-[#66c0f4] hover:underline">
          <BookOpen className="h-4 w-4" aria-hidden />
          API reference
        </a>
        <a href="/api/v1/openapi.json" className="inline-flex items-center gap-1.5 font-medium text-[#66c0f4] hover:underline">
          <FileJson className="h-4 w-4" aria-hidden />
          OpenAPI 3.1 spec
        </a>
      </div>
    </section>
  );
}
